<template>
    <div>
        <v-alert v-if="template.status === 'rejected' && template.rejection_reason" type="error" outlined dense class="mb-4">
            <span class="font-weight-medium">Rejeitado pelo AJ:</span> {{ template.rejection_reason }}
        </v-alert>
        <v-alert v-else-if="template.status === 'pending_approval'" type="warning" outlined dense class="mb-4">
            Enviada pela recuperanda, aguardando a análise do Administrador Judicial.
        </v-alert>
        <v-alert v-else-if="!editable" type="info" outlined dense class="mb-4">
            Somente leitura (status: {{ statusLabel.toLowerCase() }}). Para alterar, crie uma nova versão.
        </v-alert>

        <ui-section title="Texto do termo" :description="editable ? 'Clique com o botão direito no texto (ou use Inserir campo) para incluir dados do credor, do crédito e do processo.' : null">
            <v-text-field v-if="editable" v-model="form.title" label="Título do termo" outlined dense maxlength="255" hide-details="auto" class="mb-4"></v-text-field>
            <plan-adhesion-editor v-if="editable" v-model="form.content_html" :catalog="catalog" :min-height="420" />
            <div v-else class="pa-document" v-html="form.content_html"></div>
            <div v-if="editable" class="ui-small ui-muted mt-2">
                Obrigatórios: <span class="font-weight-medium">Código do termo</span> e <span class="font-weight-medium">Opção de pagamento (texto completo)</span>. O texto da opção escolhida pelo credor entra no lugar do campo.
            </div>
        </ui-section>

        <ui-section title="Opções de pagamento" :description="`${form.payment_options.length} opção(ões). Cada classe votante com crédito precisa de pelo menos uma.`" class="mt-4">
            <div v-for="classItem in classes" :key="classItem.id" class="pa-option-group">
                <div class="pa-option-group__header">
                    <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                        <span class="font-weight-medium">{{ classItem.name }}</span>
                        <ui-badge set="adhesion" v-if="classItem.required" :tone="optionsOf(classItem.id).length ? 'success' : 'danger'"
                            :text="optionsOf(classItem.id).length ? 'Obrigatória' : 'Obrigatória · sem opção'" />
                        <span class="ui-small ui-muted">{{ optionsOf(classItem.id).length }} opção(ões)</span>
                    </div>
                    <v-btn v-if="(editable) && $allowed('create')" small outlined @click="addOption(classItem.id)"><v-icon left small>mdi-plus</v-icon>Adicionar opção</v-btn>
                </div>
                <div v-for="(option, index) in optionsOf(classItem.id)" :key="option.uid" class="pa-option">
                    <template v-if="editable">
                        <div class="d-flex align-center mb-3" style="gap: 6px;">
                            <v-text-field v-model="option.title" label="Título da opção" dense outlined hide-details maxlength="255"></v-text-field>
                            <v-btn icon small title="Subir" :disabled="index === 0" @click="moveOption(option, -1)"><v-icon small>mdi-arrow-up</v-icon></v-btn>
                            <v-btn icon small title="Descer" :disabled="index === optionsOf(classItem.id).length - 1" @click="moveOption(option, 1)"><v-icon small>mdi-arrow-down</v-icon></v-btn>
                            <v-btn v-if="$allowed('delete')" icon small title="Remover" @click="removeOption(option)"><v-icon small color="#dc2626">mdi-trash-can-outline</v-icon></v-btn>
                        </div>
                        <plan-adhesion-editor v-model="option.content_html" :catalog="catalog" :exclude-keys="optionExcludedKeys" :min-height="120" />
                    </template>
                    <template v-else>
                        <div class="font-weight-medium mb-1">{{ option.title }}</div>
                        <div class="pa-document pa-document--compact" v-html="option.content_html"></div>
                    </template>
                </div>
                <div v-if="!optionsOf(classItem.id).length" class="ui-small ui-muted pa-option-empty">Nenhuma opção cadastrada.</div>
            </div>
        </ui-section>
    </div>
</template>

<script>
import PlanAdhesionEditor from "./PlanAdhesionEditor.vue";
import { STATUS_LABELS } from "./plan-adhesion.util";

let uidSeed = 0;
const withUid = (option) => ({ ...option, uid: `opt-${++uidSeed}` });

// Formulario de uma versao do termo: texto + opcoes de pagamento por classe.
// As acoes (salvar, enviar, aprovar, publicar, previa) ficam na barra do editor
// (PlanAdhesionTemplates), que chama emitAction; aqui so sai o conteudo atual.
export default {
    name: "PlanAdhesionTemplateForm",

    components: { PlanAdhesionEditor },

    props: {
        template: { type: Object, required: true },
        catalog: { type: Array, default: () => [] },
        classNames: { type: Object, default: () => ({}) },
        votingClassTypes: { type: Array, default: () => [] },
        editable: { type: Boolean, default: false },
    },

    data: () => ({
        form: { title: "", content_html: "", payment_options: [] },
        baseline: "",
        optionExcludedKeys: ["opcao_pagamento.texto", "opcao_pagamento.titulo"],
    }),

    computed: {
        statusLabel() {
            return STATUS_LABELS[this.template.status] || this.template.status || "";
        },
        classes() {
            const ids = new Set([
                ...Object.keys(this.classNames).map(Number),
                ...this.votingClassTypes.map(Number),
                ...this.form.payment_options.map((option) => Number(option.class_type)),
            ]);
            return [...ids].sort((a, b) => a - b).map((id) => ({
                id,
                name: this.classNames[id] || `Classe ${id}`,
                required: this.votingClassTypes.map(Number).includes(id),
            }));
        },
    },

    watch: {
        "template.id": { immediate: true, handler() { this.reset(); } },
        "template.updated_at"() { this.reset(); },
        form: {
            deep: true,
            handler() { this.$emit("dirty", JSON.stringify(this.payload()) !== this.baseline); },
        },
    },

    methods: {
        reset() {
            this.form = {
                title: this.template.title || "",
                content_html: this.template.content_html || "",
                payment_options: (this.template.payment_options || [])
                    .slice()
                    .sort((a, b) => (a.class_type - b.class_type) || (a.sort_order - b.sort_order))
                    .map(withUid),
            };
            this.baseline = JSON.stringify(this.payload());
        },

        optionsOf(classType) {
            return this.form.payment_options.filter((option) => Number(option.class_type) === Number(classType));
        },

        addOption(classType) {
            const letter = String.fromCharCode(65 + this.optionsOf(classType).length);
            this.form.payment_options.push(withUid({ class_type: classType, title: `Opção ${letter}`, content_html: "" }));
        },

        removeOption(option) {
            this.form.payment_options = this.form.payment_options.filter((item) => item.uid !== option.uid);
        },

        moveOption(option, direction) {
            const siblings = this.optionsOf(option.class_type);
            const target = siblings[siblings.indexOf(option) + direction];
            if (!target) return;
            const list = this.form.payment_options.slice();
            const a = list.indexOf(option);
            const b = list.indexOf(target);
            [list[a], list[b]] = [list[b], list[a]];
            this.form.payment_options = list;
        },

        payload() {
            const order = {};
            return {
                title: this.form.title,
                content_html: this.form.content_html,
                payment_options: this.form.payment_options.map((option) => {
                    order[option.class_type] = (order[option.class_type] || 0) + 1;
                    return { class_type: option.class_type, title: option.title, content_html: option.content_html, sort_order: order[option.class_type] - 1 };
                }),
            };
        },

        emitAction(action) {
            this.$emit("action", action.key, this.payload());
        },
    },
};
</script>
